import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { MacroDonut, MacroBar } from "@/components/Macros";
import { Button } from "@/components/ui/button";
import { Flame, Activity, Target, Info, ArrowRight } from "lucide-react";

function StatCard({ icon: Icon, label, value, unit, desc }) {
  return (
    <div className="chenia-card p-6" data-testid={`goal-stat-${label}`}>
      <span className="grid place-items-center h-11 w-11 rounded-2xl bg-secondary text-primary"><Icon className="h-5 w-5" /></span>
      <p className="mt-4 text-sm text-muted-foreground">{label}</p>
      <p className="font-serif text-3xl font-semibold mt-1">{value} <span className="text-lg text-muted-foreground">{unit}</span></p>
      <p className="mt-1 text-xs text-muted-foreground">{desc}</p>
    </div>
  );
}

export default function Goals() {
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["goals"], queryFn: async () => (await api.get("/nutrition/goals")).data });
  const goals = data?.goals;

  return (
    <AppShell>
      <div className="chenia-fade-up">
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{t("goals_title")}</h1>
        <p className="text-muted-foreground mt-1">{t("goals_sub")}</p>
      </div>

      {!data?.complete ? (
        <div className="chenia-card p-8 mt-8 text-center" data-testid="goals-incomplete">
          <h2 className="text-xl font-semibold">{t("complete_profile")}</h2>
          <Link to="/onboarding"><Button className="mt-4 rounded-full bg-primary hover:bg-chenia-terracottadark">{t("complete_onboarding")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-5 mt-8">
            <StatCard icon={Flame} label={t("bmr")} value={goals.bmr} unit={t("kcal")} desc={t("bmr_desc")} />
            <StatCard icon={Activity} label={t("tdee")} value={goals.tdee} unit={t("kcal")} desc={`${t("tdee_desc")} · ×${goals.activity_factor}`} />
            <StatCard icon={Target} label={t("target_calories")} value={goals.target_calories} unit={t("kcal")} desc={`${t(goals.goal)} · ${goals.calorie_adjustment >= 0 ? "+" : ""}${goals.calorie_adjustment} ${t("kcal")}`} />
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <div className="chenia-card p-6 flex flex-col items-center" data-testid="goals-donut">
              <p className="self-start text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-2">{t("target_calories")}</p>
              <MacroDonut value={goals.target_calories} max={goals.target_calories} label={t("per_day")} sublabel={t("kcal")} />
            </div>
            <div className="chenia-card p-6" data-testid="goals-macros">
              <p className="text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-5">{t("macro_distribution")}</p>
              <div className="space-y-5">
                <MacroBar label={`${t("protein")}`} value={goals.protein_g} unit="g" pct={goals.protein_pct} color="#C87D65" />
                <MacroBar label={`${t("carbs")}`} value={goals.carbs_g} unit="g" pct={goals.carbs_pct} color="#A8BBA2" />
                <MacroBar label={`${t("fat")}`} value={goals.fat_g} unit="g" pct={goals.fat_pct} color="#E8B4B8" />
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2 text-center">
                {[["protein", goals.protein_kcal], ["carbs", goals.carbs_kcal], ["fat", goals.fat_kcal]].map(([k, v]) => (
                  <div key={k} className="rounded-2xl bg-secondary py-3">
                    <p className="text-xs text-muted-foreground">{t(k)}</p>
                    <p className="font-semibold">{v} {t("kcal")}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="chenia-card p-6 mt-5 flex gap-4" data-testid="goals-method">
            <span className="grid place-items-center h-11 w-11 shrink-0 rounded-2xl bg-chenia-rosesoft text-chenia-terracottadark"><Info className="h-5 w-5" /></span>
            <div>
              <h3 className="font-semibold">{t("method_title")}</h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{t("method_desc")}</p>
            </div>
          </div>
        </>
      )}

      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{t("disclaimer")}</p>
    </AppShell>
  );
}
